import { useState } from "react";

import { getTextFieldVisualState, type TextFieldVisualState } from "../../atoms/text-field";
import { DEFAULT_PHONE_NUMBER_FIELD_COUNTRIES } from "./countries";
import {
  buildPhoneNumberState,
  ensurePhoneNumberWithPlus,
  formatPhoneNumberForDisplay,
  getCountryByCode,
  inferCountryFromPhoneNumber,
} from "./lib";
import type { PhoneNumberFieldCountry, PhoneNumberFieldProps } from "./types";

interface UsePhoneNumberFieldParams extends Pick<
  PhoneNumberFieldProps,
  "countries" | "initialCountryCode" | "value" | "onChangePhoneNumber" | "disabled" | "state"
> {
  isFocused: boolean;
  open: boolean;
}

interface UsePhoneNumberFieldResult {
  availableCountries: PhoneNumberFieldCountry[];
  resolvedCountry: PhoneNumberFieldCountry;
  displayValue: string;
  visualState: TextFieldVisualState;
  handleInputChange: (text: string) => void;
  handleCountrySelect: (nextCountry: PhoneNumberFieldCountry) => void;
}

/**
 * Shared controller for the phone-number field — owns country resolution and the
 * input/selection handlers so the web (dropdown) and native (bottom sheet)
 * surfaces stay behaviourally identical. The `open`/`isFocused` flags are owned
 * by each surface and passed in, since the surfaces present differently.
 */
export function usePhoneNumberField({
  countries,
  initialCountryCode,
  value,
  onChangePhoneNumber,
  disabled = false,
  state = "default",
  isFocused,
  open,
}: UsePhoneNumberFieldParams): UsePhoneNumberFieldResult {
  const availableCountries: PhoneNumberFieldCountry[] = countries?.length
    ? countries
    : DEFAULT_PHONE_NUMBER_FIELD_COUNTRIES;
  const fallbackCountryCode = initialCountryCode ?? availableCountries[0]?.code ?? "SG";

  const [internalCountryCode, setInternalCountryCode] = useState(fallbackCountryCode);

  const currentPhoneNumber = value ?? "";
  const pickedCountry = getCountryByCode(availableCountries, internalCountryCode);
  const inferredCountry = inferCountryFromPhoneNumber(
    currentPhoneNumber,
    availableCountries,
    pickedCountry,
  );
  const resolvedCountry: PhoneNumberFieldCountry =
    inferredCountry ?? pickedCountry ?? availableCountries[0]!;

  const visualState = getTextFieldVisualState({
    disabled,
    isFocused: isFocused || open,
    state,
  });
  const displayValue = formatPhoneNumberForDisplay(currentPhoneNumber);

  const handleInputChange = (text: string) => {
    const trimmed = text.trim();
    const nextCountry = trimmed.startsWith("+")
      ? (inferCountryFromPhoneNumber(trimmed, availableCountries, resolvedCountry) ??
        resolvedCountry)
      : resolvedCountry;
    const nextState = buildPhoneNumberState(text, nextCountry);

    if (nextCountry.code !== internalCountryCode) {
      setInternalCountryCode(nextCountry.code);
    }

    onChangePhoneNumber?.(nextState.phoneNumber);
  };

  const handleCountrySelect = (nextCountry: PhoneNumberFieldCountry) => {
    setInternalCountryCode(nextCountry.code);

    const normalized = ensurePhoneNumberWithPlus(currentPhoneNumber);
    const digits = normalized.replace(/\D/g, "");
    const callingDigits = resolvedCountry.dialCode.replace(/\D/g, "");
    const nationalDigits = digits.startsWith(callingDigits)
      ? digits.slice(callingDigits.length)
      : "";
    // With no number yet, a pick only moves the flag: the value stays empty
    // rather than becoming a bare calling code, which isn't E.164 and passes a
    // "not empty" check (PhoneNumberField #4)
    if (!nationalDigits) {
      if (currentPhoneNumber !== "") onChangePhoneNumber?.("");
      return;
    }

    onChangePhoneNumber?.(buildPhoneNumberState(nationalDigits, nextCountry).phoneNumber);
  };

  return {
    availableCountries,
    resolvedCountry,
    displayValue,
    visualState,
    handleInputChange,
    handleCountrySelect,
  };
}
